import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../lib/AuthContext'
import { supabase } from '../lib/supabase'
import {
  getBarriosActivos, crearPedido,
} from '../lib/portalClienteApi'
import { proximasFechasEntrega, calcularEnvio } from '../lib/envios'
import { geocodificar, validarBarrioConDireccion } from '../lib/geo'
import MapPin from './MapPin'

/**
 * Componente reusable: 4 pasos checkout para Kit suelto y La Tienda.
 *
 * Props:
 *   tipo            — 'compra_unica' | 'productos_sueltos' | 'adicional_suscripcion'
 *   kitCodigo       — opcional, sólo si lleva un kit
 *   itemsIniciales  — array {sku, nombre, presentacion, precio_unit, cantidad}
 *   subtotalKit     — opcional, precio del kit (si tipo='compra_unica')
 *   esSuscriptor    — bool (true desactiva mensaje invitado)
 *   onCancelar      — callback () → void
 *   onCompletado    — callback (pedido) → void
 *   tituloHero      — string para el hero ("Probá nuestro Kit Familiar", etc)
 */
export default function CheckoutFlow({
  tipo, kitCodigo = null, itemsIniciales = [], subtotalKit = 0,
  esSuscriptor = false, direccionSuscriptor = null,
  onCancelar, onCompletado,
  tituloHero = 'Tu pedido',
}) {
  const { user, profile } = useAuth()
  const navigate = useNavigate()
  const [paso, setPaso] = useState(1)  // 1 resumen · 2 dirección · 3 día · 4 pago
  const [barrios, setBarrios] = useState([])
  const [items, setItems] = useState(itemsIniciales)
  const [coords, setCoords] = useState(null)
  const [coordsExactas, setCoordsExactas] = useState(false)
  const [geocodando, setGeocodando] = useState(false)
  const [validacionBarrio, setValidacionBarrio] = useState(null)  // { ok, sinCobertura, sugerenciaActivo, mensaje }
  const [editandoDireccion, setEditandoDireccion] = useState(false)  // suscriptor: cambiar dirección de siempre
  const [opcionesFecha, setOpcionesFecha] = useState([])
  const [cargandoFechas, setCargandoFechas] = useState(false)
  const [creando, setCreando] = useState(false)
  const [err, setErr] = useState(null)

  const [form, setForm] = useState({
    // Dirección
    barrioId: '',
    direccion: '',
    apto: '',
    tipoVivienda: '',
    destinatarioNombre: '',
    destinatarioTelefono: '',
    // Día y horario
    fechaEntrega: '',          // 'YYYY-MM-DD'
    diaSemana: '',             // 'lunes' | etc
    fueraDeCorte: false,
    ventana: 'manana',
    // Pago
    metodoPago: 'transferencia',
    compradorNombre: profile?.nombre || '',
    compradorTelefono: profile?.phone || '',
    compradorEmail: profile?.email || user?.email || '',
    compradorPassword: '',  // solo para visitor signup on-the-fly
    notas: '',
  })

  const esVisitor = !user

  // Cargar barrios al montar
  useEffect(() => {
    getBarriosActivos().then(setBarrios)
  }, [])

  // Si es suscriptor, prerelleno con su dirección "de siempre"
  useEffect(() => {
    if (!direccionSuscriptor) return
    setForm(f => ({
      ...f,
      barrioId: direccionSuscriptor.barrioId || f.barrioId,
      direccion: direccionSuscriptor.direccion || f.direccion,
      apto: direccionSuscriptor.apto || f.apto,
      tipoVivienda: direccionSuscriptor.tipoVivienda || f.tipoVivienda,
    }))
    if (direccionSuscriptor.lat != null && direccionSuscriptor.lng != null) {
      setCoords({ lat: direccionSuscriptor.lat, lng: direccionSuscriptor.lng })
      setCoordsExactas(true)
    }
  }, [direccionSuscriptor])

  // Sincronizar perfil del user logueado
  useEffect(() => {
    if (profile && !form.compradorNombre) {
      setForm(f => ({
        ...f,
        compradorNombre: profile.nombre || f.compradorNombre,
        compradorTelefono: profile.phone || f.compradorTelefono,
        compradorEmail: profile.email || user?.email || f.compradorEmail,
      }))
    }
  }, [profile, user])

  const barrioElegido = barrios.find(b => b.id === form.barrioId)

  // Subtotal calculado
  const subtotalItems = useMemo(
    () => items.reduce((s, it) => s + Number(it.precio_unit) * Number(it.cantidad || 1), 0),
    [items]
  )
  const subtotal = subtotalItems + Number(subtotalKit || 0)

  // Cargar opciones de fecha cuando cambia barrio (incluye dias fuera de ruta)
  useEffect(() => {
    if (paso !== 3 || !form.barrioId) return
    setCargandoFechas(true)
    proximasFechasEntrega({ barrioId: form.barrioId, maxOpciones: 8, soloEnRuta: false })
      .then(setOpcionesFecha)
      .finally(() => setCargandoFechas(false))
  }, [paso, form.barrioId])

  // Helper: calcular envio en preview (frontend)
  function calcEnvioPreview(opcion) {
    if (!barrioElegido || !opcion) return { costoEnvio: 0, enRuta: true }
    return calcularEnvio({
      esSuscriptor,
      tipo,
      barrio: barrioElegido,
      fecha: opcion.fechaEntrega,
      monto: subtotal,
      fueraDeCorte: opcion.fueraDeCorte,
    })
  }

  async function geocodear() {
    setErr(null); setCoordsExactas(false); setValidacionBarrio(null)
    if (!barrioElegido || !form.direccion.trim()) return
    setGeocodando(true)
    try {
      // Geocoding SIN forzar el barrio en la query (para que detecte el real)
      const r = await geocodificar(form.direccion)
      if (r) {
        setCoords({ lat: r.lat, lng: r.lng })
        setCoordsExactas(true)
        // Validar que el barrio detectado coincida con el elegido
        const v = validarBarrioConDireccion(r.barrioDetectado, barrioElegido.nombre, barrios)
        setValidacionBarrio(v)
        return
      }
      // Fallback: centroide del barrio
      const rb = await geocodificar(barrioElegido.nombre)
      if (rb) setCoords({ lat: rb.lat, lng: rb.lng })
    } finally { setGeocodando(false) }
  }

  function aplicarSugerencia() {
    if (!validacionBarrio?.sugerenciaActivo) return
    setForm(f => ({ ...f, barrioId: validacionBarrio.sugerenciaActivo.id }))
    setValidacionBarrio(null)
  }

  function toggleItemCantidad(sku, delta) {
    setItems(prev => prev
      .map(it => it.sku === sku ? { ...it, cantidad: Math.max(0, Number(it.cantidad || 1) + delta) } : it)
      .filter(it => it.cantidad > 0)
    )
  }

  function elegirFecha(opcion) {
    setForm(f => ({
      ...f,
      fechaEntrega: opcion.fechaEntrega,
      diaSemana: opcion.nombreDia,
      fueraDeCorte: opcion.fueraDeCorte,
    }))
  }

  async function confirmar() {
    setErr(null); setCreando(true)
    try {
      if (!form.barrioId) throw new Error('Falta barrio')
      if (!form.direccion.trim()) throw new Error('Falta dirección')
      if (!form.fechaEntrega) throw new Error('Falta elegir día')
      if (!form.compradorNombre.trim()) throw new Error('Falta tu nombre')
      const tel = form.compradorTelefono.replace(/\D/g, '')
      if (tel.length < 8) throw new Error('Falta un WhatsApp válido (mínimo 8 dígitos)')
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.compradorEmail.trim())) {
        throw new Error('Falta un email válido')
      }

      // VISITOR: crear cuenta + pedido + preference MP en una sola llamada (edge publica)
      if (esVisitor) {
        if (!form.compradorPassword || form.compradorPassword.length < 6) {
          throw new Error('Creá una contraseña de al menos 6 caracteres')
        }
        const { data: visitorData, error: visErr } = await supabase.functions.invoke('crear-pedido-publico', {
          body: {
            email: form.compradorEmail.trim().toLowerCase(),
            password: form.compradorPassword,
            nombre: form.compradorNombre.trim(),
            telefono: form.compradorTelefono,
            pedido: {
              tipo,
              kit_codigo: kitCodigo,
              items: items.map(it => ({
                sku: it.sku, nombre: it.nombre, presentacion: it.presentacion,
                precio_unit: Number(it.precio_unit), cantidad: Number(it.cantidad || 1),
                marca: it.marca,
              })),
              comprador_nombre: form.compradorNombre.trim(),
              comprador_telefono: form.compradorTelefono,
              comprador_email: form.compradorEmail.trim().toLowerCase(),
              entrega_barrio_id: form.barrioId,
              entrega_direccion: form.direccion,
              entrega_apto: form.apto,
              entrega_lat: coords?.lat,
              entrega_lng: coords?.lng,
              entrega_dia_semana: form.diaSemana,
              entrega_ventana: form.ventana,
              monto_subtotal: subtotal,
              costo_envio: costoEnvio,
              fecha_entrega_programada: form.fechaEntrega,
              notas: form.notas,
            },
          },
        })
        if (visErr) throw visErr
        if (visitorData?.error) throw new Error(visitorData.error + (visitorData.detalle ? ': ' + JSON.stringify(visitorData.detalle) : ''))
        // Redirigir al checkout MP
        const initPoint = visitorData.init_point || visitorData.sandbox_init_point
        if (!initPoint) throw new Error('MP no devolvió URL de pago')
        // Guardar email del visitor en localStorage para mostrar mensaje al volver
        try { localStorage.setItem('limpack_visitor_email', form.compradorEmail.trim().toLowerCase()) } catch {}
        window.location.href = initPoint
        return
      }

      const pedido = await crearPedido({
        tipo,
        kitCodigo,
        items: items.map(it => ({
          sku: it.sku, nombre: it.nombre, presentacion: it.presentacion,
          precio_unit: Number(it.precio_unit), cantidad: Number(it.cantidad || 1),
          marca: it.marca,
        })),
        barrioId: form.barrioId,
        direccion: form.direccion,
        apto: form.apto,
        lat: coords?.lat,
        lng: coords?.lng,
        diaSemana: form.diaSemana,
        ventana: form.ventana,
        fechaEntrega: form.fechaEntrega,
        fueraDeCorte: form.fueraDeCorte,
        destinatarioNombre: form.destinatarioNombre,
        destinatarioTelefono: form.destinatarioTelefono,
        compradorNombre: form.compradorNombre,
        compradorTelefono: form.compradorTelefono,
        compradorEmail: form.compradorEmail,
        metodoPago: 'mercadopago',  // forzamos siempre MP
        notas: form.notas,
      })
      // Redirigir a la pantalla de pago MP con el pedido_id
      if (pedido?.id) {
        navigate(`/cliente/pagar-pedido/${pedido.id}`, { replace: true })
      } else {
        onCompletado?.(pedido)
      }
    } catch (e) {
      setErr(e.message || String(e))
      setCreando(false)
    }
  }

  // Costo envío del fecha elegida (para mostrar en paso 4)
  const opcionElegida = opcionesFecha.find(o => o.fechaEntrega === form.fechaEntrega)
  const previewElegida = opcionElegida ? calcEnvioPreview(opcionElegida) : { costoEnvio: 0 }
  const costoEnvio = previewElegida.costoEnvio
  const totalFinal = subtotal + costoEnvio

  return (
    <div className="cf-shell">
      {/* HERO */}
      <div className="cf-hero">
        <div className="cf-hero-pill">
          <span className="cf-hero-dot"></span>
          Paso {paso} de 4 · Tu pedido
        </div>
        <h1>{tituloHero}</h1>
      </div>

      {/* Stepper — pasos previos clickeables para volver atras */}
      <div className="cf-stepper">
        {['Resumen', 'Dirección', 'Día', 'Pagar'].map((label, i) => {
          const idx = i + 1
          const isDone = paso > idx
          const isActive = paso === idx
          const clickeable = isDone  // solo pasos completados
          return (
            <button
              type="button"
              key={i}
              disabled={!clickeable}
              onClick={() => clickeable && setPaso(idx)}
              className={`cf-step ${isActive ? 'active' : ''} ${isDone ? 'done' : ''}`}
              style={{
                background: 'transparent', border: 0, font: 'inherit',
                cursor: clickeable ? 'pointer' : 'default',
                padding: 0,
              }}>
              <div className="cf-step-num">{isDone ? '✓' : idx}</div>
              <div className="cf-step-label">{label}</div>
            </button>
          )
        })}
      </div>

      {/* PASO 1: Resumen items */}
      {paso === 1 && (
        <div className="cf-card">
          <h2 className="cf-h2">Lo que llevás</h2>
          {kitCodigo && (
            <div className="cf-kit-row">
              <div>
                <div className="cf-kit-tag">📦 KIT</div>
                <div className="cf-kit-nombre">{kitCodigo.replace(/_/g, ' ').toUpperCase()}</div>
                <div className="cf-kit-sub">Kit completo · una sola vez</div>
              </div>
              <div className="cf-kit-precio">${Number(subtotalKit).toLocaleString('es-UY')}</div>
            </div>
          )}
          {items.length > 0 && (
            <ul className="cf-items">
              {items.map(it => (
                <li key={it.sku}>
                  <div className="cf-item-info">
                    <strong>{it.nombre}</strong>
                    {it.presentacion && <span> · {it.presentacion}</span>}
                    {it.marca && <span className="cf-item-marca">{it.marca}</span>}
                  </div>
                  <div className="cf-item-cant">
                    <button onClick={() => toggleItemCantidad(it.sku, -1)} aria-label="Quitar uno">−</button>
                    <span>{it.cantidad}</span>
                    <button onClick={() => toggleItemCantidad(it.sku, +1)} aria-label="Agregar uno">+</button>
                  </div>
                  <div className="cf-item-precio">${(Number(it.precio_unit) * Number(it.cantidad)).toLocaleString('es-UY')}</div>
                </li>
              ))}
            </ul>
          )}
          {(!kitCodigo && items.length === 0) && (
            <p className="cf-empty">Tu carrito está vacío. <button className="cf-link" onClick={onCancelar}>← Volver</button></p>
          )}

          <div className="cf-totales">
            <div><span>Subtotal</span><strong>${subtotal.toLocaleString('es-UY')}</strong></div>
            <div className="cf-totales-info">Precios con IVA incluido. El envío se calcula en el siguiente paso según tu barrio y día.</div>
          </div>

          <div className="cf-nav">
            <button className="cf-btn-back" onClick={onCancelar}>← Cancelar</button>
            <button className="cf-btn-next" onClick={() => setPaso(2)} disabled={subtotal <= 0}>Siguiente →</button>
          </div>
        </div>
      )}

      {/* PASO 2: Dirección */}
      {paso === 2 && (
        <div className="cf-card">
          <h2 className="cf-h2">¿A dónde te lo enviamos?</h2>

          {/* Suscriptor: cartel "tu dirección de siempre" — si no cliquea cambiar, no muestra el form */}
          {direccionSuscriptor && !editandoDireccion ? (
            <>
              <div className="cf-direccion-siempre">
                <div className="cf-direccion-siempre-icon">📍</div>
                <div className="cf-direccion-siempre-body">
                  <div className="cf-direccion-siempre-tag">Tu dirección de siempre</div>
                  <div className="cf-direccion-siempre-linea">
                    {direccionSuscriptor.direccion}
                    {direccionSuscriptor.apto ? `, ${direccionSuscriptor.apto}` : ''}
                  </div>
                  <div className="cf-direccion-siempre-barrio">
                    {direccionSuscriptor.barrioNombre}
                  </div>
                </div>
              </div>
              <button type="button" className="cf-link-cambiar" onClick={() => setEditandoDireccion(true)}>
                ✏️ Enviarlo a otra dirección
              </button>

              <div className="cf-nav">
                <button className="cf-btn-back" onClick={() => setPaso(1)}>← Atrás</button>
                <button className="cf-btn-next" onClick={() => setPaso(3)}
                  disabled={!form.barrioId || !form.direccion.trim()}>
                  Siguiente →
                </button>
              </div>
            </>
          ) : (
          <>
          {direccionSuscriptor && (
            <div className="cf-aviso-otra-direccion">
              <span>📦 Vas a enviarlo a otra dirección distinta a tu dirección de siempre.</span>
              <button type="button" onClick={() => {
                setEditandoDireccion(false)
                // restaurar dirección de siempre
                setForm(f => ({
                  ...f,
                  barrioId: direccionSuscriptor.barrioId || '',
                  direccion: direccionSuscriptor.direccion || '',
                  apto: direccionSuscriptor.apto || '',
                  tipoVivienda: direccionSuscriptor.tipoVivienda || '',
                }))
                if (direccionSuscriptor.lat != null) {
                  setCoords({ lat: direccionSuscriptor.lat, lng: direccionSuscriptor.lng })
                  setCoordsExactas(true)
                }
                setValidacionBarrio(null)
              }}>← Usar la de siempre</button>
            </div>
          )}

          <div className="cf-row-2">
            <Field label="Barrio" required>
              <select value={form.barrioId} onChange={e => setForm({ ...form, barrioId: e.target.value })}>
                <option value="">— elegir —</option>
                {barrios.map(b => <option key={b.id} value={b.id}>{b.nombre}</option>)}
              </select>
            </Field>
            <Field label="Tipo de vivienda">
              <select value={form.tipoVivienda} onChange={e => setForm({ ...form, tipoVivienda: e.target.value })}>
                <option value="">— elegir —</option>
                <option value="casa">Casa</option>
                <option value="edificio">Edificio</option>
                <option value="oficina">Oficina</option>
              </select>
            </Field>
          </div>

          {!form.barrioId && barrios.length > 0 && (
            <div className="cf-info">Por ahora cubrimos: {barrios.map(b => b.nombre).join(', ')}.</div>
          )}

          <div className="cf-row-2-3">
            <Field label="Calle y número" required>
              <input type="text" value={form.direccion}
                onChange={e => setForm({ ...form, direccion: e.target.value })}
                onBlur={geocodear} placeholder="Cavia 2858" />
            </Field>
            <Field label="Apto / Piso">
              <input type="text" value={form.apto}
                onChange={e => setForm({ ...form, apto: e.target.value })} placeholder="Apto 406" />
            </Field>
          </div>

          <button type="button" onClick={geocodear} disabled={!form.barrioId || geocodando} className="cf-btn-secondary">
            {geocodando ? 'Buscando…' : coords ? 'Recargar mapa' : 'Verificar en mapa'}
          </button>

          {/* Validación barrio detectado vs barrio elegido */}
          {validacionBarrio && !validacionBarrio.ok && (
            <div className={`cf-warn-barrio ${validacionBarrio.sinCobertura ? 'is-bloqueo' : 'is-warn'}`}>
              <div className="cf-warn-barrio-icon">{validacionBarrio.sinCobertura ? '🚫' : '⚠️'}</div>
              <div className="cf-warn-barrio-body">
                <strong>{validacionBarrio.mensaje}</strong>
                {validacionBarrio.sinCobertura ? (
                  <>
                    <p>Por ahora no llegamos a esa zona. Si querés, te avisamos cuando lleguemos:</p>
                    <a
                      href={`https://wa.me/59898610001?text=${encodeURIComponent('Hola! Mi dirección es ' + form.direccion + '. Me gustaría saber cuándo llegan a mi zona con Limpack.')}`}
                      target="_blank" rel="noopener" className="cf-warn-cta"
                    >
                      📲 Avisame por WhatsApp
                    </a>
                  </>
                ) : validacionBarrio.sugerenciaActivo ? (
                  <>
                    <p>Para continuar, cambiá el barrio elegido o corregí la dirección.</p>
                    <button type="button" className="cf-warn-cta" onClick={aplicarSugerencia}>
                      ✓ Cambiar a {validacionBarrio.sugerenciaActivo.nombre}
                    </button>
                  </>
                ) : null}
              </div>
            </div>
          )}

          {coords && !validacionBarrio?.sinCobertura && (
            <>
              <div style={{ marginTop: 12 }}>
                <MapPin lat={coords.lat} lng={coords.lng} onChange={(lat, lng) => { setCoords({ lat, lng }); setCoordsExactas(false) }} height={220} />
              </div>
              <label className={`cf-confirm-pin ${coordsExactas ? 'ok' : 'pend'}`}>
                <input type="checkbox" checked={coordsExactas} onChange={e => setCoordsExactas(e.target.checked)} />
                <span>{coordsExactas ? 'Pin en la ubicación correcta' : 'Confirmá el pin antes de seguir'}</span>
              </label>
            </>
          )}

          {tipo === 'adicional_suscripcion' && (
            <>
              <h3 className="cf-h3">¿Es para otra persona?</h3>
              <div className="cf-row-2">
                <Field label="Destinatario (opcional)">
                  <input type="text" value={form.destinatarioNombre}
                    onChange={e => setForm({ ...form, destinatarioNombre: e.target.value })}
                    placeholder="Mamá, oficina, etc." />
                </Field>
                <Field label="Teléfono destinatario">
                  <input type="tel" value={form.destinatarioTelefono}
                    onChange={e => setForm({ ...form, destinatarioTelefono: e.target.value })}
                    placeholder="099 xxx xxx" />
                </Field>
              </div>
            </>
          )}

          <div className="cf-nav">
            <button className="cf-btn-back" onClick={() => setPaso(1)}>← Atrás</button>
            <button className="cf-btn-next"
              onClick={() => setPaso(3)}
              disabled={
                !form.barrioId
                || !form.direccion.trim()
                || (validacionBarrio && !validacionBarrio.ok)
                || (coords && !validacionBarrio?.sinCobertura && !coordsExactas)
              }>
              Siguiente →
            </button>
          </div>
          </>
          )}
        </div>
      )}

      {/* PASO 3: Día */}
      {paso === 3 && (
        <div className="cf-card">
          <h2 className="cf-h2">¿Cuándo te lo enviamos?</h2>

          {/* Banner compacto suscriptor / no-suscriptor */}
          {esSuscriptor ? (
            <div className="cf-banner-susc">
              <div className="cf-banner-icon">⭐</div>
              <div>
                <strong>Sos suscriptor — envío gratis los días de tu ruta.</strong>
                <span> En <strong>{barrioElegido?.nombre}</strong> pasamos{' '}
                <strong style={{ textTransform: 'capitalize' }}>{(barrioElegido?.dias || []).join(' y ')}</strong>.</span>
              </div>
            </div>
          ) : (
            <div className="cf-banner-info">
              <div className="cf-banner-icon">💡</div>
              <div>
                <strong>En {barrioElegido?.nombre} pasamos{' '}
                  <span style={{ textTransform: 'capitalize' }}>{(barrioElegido?.dias || []).join(' y ')}</span>.
                </strong>
              </div>
            </div>
          )}

          {cargandoFechas && <div className="cf-loading">Buscando próximas fechas…</div>}

          {!cargandoFechas && opcionesFecha.length === 0 && (
            <div className="cf-info">No hay fechas disponibles para este barrio.</div>
          )}

          {/* Selector custom compacto (controlamos font/spacing, no el picker nativo) */}
          {!cargandoFechas && opcionesFecha.length > 0 && (
            <>
              <Field label="Día de entrega" required>
                <FechaDropdown
                  opciones={opcionesFecha}
                  valor={form.fechaEntrega}
                  onSeleccionar={elegirFecha}
                  calcEnvio={(o) => calcEnvioPreview(o).costoEnvio}
                />
              </Field>

              {/* Resumen del día elegido */}
              {opcionElegida && (
                <div className={`cf-fecha-resumen ${opcionElegida.enRuta ? 'is-en-ruta' : 'is-fuera'}`}>
                  <div className="cf-fecha-resumen-info">
                    <div className="cf-fecha-resumen-tag">
                      {opcionElegida.enRuta ? '🟢 En tu ruta normal' : '🟡 Viaje especial fuera de ruta'}
                    </div>
                    <div className="cf-fecha-resumen-dia">{formatFechaLarga(opcionElegida.fechaEntrega)}</div>
                  </div>
                  <div className="cf-fecha-resumen-precio">
                    {previewElegida.costoEnvio > 0
                      ? <span>+${previewElegida.costoEnvio.toLocaleString('es-UY')} envío</span>
                      : <span className="cf-monto-ok">Envío gratis</span>}
                  </div>
                </div>
              )}
            </>
          )}

          <Field label="Ventana horaria">
            <select value={form.ventana} onChange={e => setForm({ ...form, ventana: e.target.value })}>
              <option value="manana">Mañana 10–14hs</option>
              <option value="tarde">Tarde 14–18hs</option>
            </select>
          </Field>

          <div className="cf-nav">
            <button className="cf-btn-back" onClick={() => setPaso(2)}>← Atrás</button>
            <button className="cf-btn-next" onClick={() => setPaso(4)} disabled={!form.fechaEntrega}>Siguiente →</button>
          </div>
        </div>
      )}

      {/* PASO 4: Pagar */}
      {paso === 4 && (
        <div className="cf-card">
          <h2 className="cf-h2">Confirmá y elegí cómo pagar</h2>

          <div className="cf-h3-row">
            <h3 className="cf-h3">Tus datos</h3>
            {esSuscriptor && profile && (
              <span className="cf-pill-cuenta">✓ Datos de tu cuenta</span>
            )}
          </div>
          <div className="cf-row-2">
            <Field label="Nombre completo" required>
              <input type="text" required value={form.compradorNombre}
                onChange={e => setForm({ ...form, compradorNombre: e.target.value })} />
            </Field>
            <Field label="WhatsApp" required>
              <input type="tel" required value={form.compradorTelefono}
                onChange={e => setForm({ ...form, compradorTelefono: e.target.value })}
                placeholder="099 xxx xxx" />
            </Field>
          </div>
          <Field label="Email" required>
            <input type="email" required value={form.compradorEmail}
              onChange={e => setForm({ ...form, compradorEmail: e.target.value })}
              placeholder="tu@email.com" />
          </Field>

          {esVisitor && (
            <>
              <Field label="Creá una contraseña" required>
                <input type="password" required minLength={6}
                  value={form.compradorPassword}
                  onChange={e => setForm({ ...form, compradorPassword: e.target.value })}
                  placeholder="Mínimo 6 caracteres" />
              </Field>
              <div className="cf-visitor-aviso" style={{ background: '#ecfdf5', borderColor: '#a7f3d0', color: '#065f46' }}>
                ✨ Te creamos tu cuenta automáticamente. Después del pago te mandamos un email para activarla y poder ver tus pedidos.
              </div>
            </>
          )}

          <p className="cf-hint">
            Necesitamos WhatsApp y email para confirmar el pedido y avisarte cuando salga el repartidor.
          </p>

          <h3 className="cf-h3">Resumen</h3>
          <div className="cf-resumen">
            <Row label="Subtotal">${subtotal.toLocaleString('es-UY')}</Row>
            <Row label="Envío">
              {costoEnvio > 0
                ? <strong>${costoEnvio.toLocaleString('es-UY')}</strong>
                : <strong style={{ color: '#15803d' }}>Gratis</strong>}
            </Row>
            <Row label="Día">
              {opcionElegida
                ? <strong>{formatFechaLarga(opcionElegida.fechaEntrega)} · {form.ventana === 'tarde' ? 'tarde' : 'mañana'}</strong>
                : '—'}
            </Row>
            <Row label="Total" big>${totalFinal.toLocaleString('es-UY')}</Row>
            <div style={{ fontSize: 11, color: 'var(--cf-muted)', textAlign: 'right', paddingTop: 4 }}>
              Todos los precios con IVA incluido
            </div>
          </div>

          <h3 className="cf-h3">Método de pago</h3>
          <div style={{
            padding: '14px 16px',
            background: '#ecfdf5', border: '1px solid #a7f3d0',
            borderRadius: 12, color: '#065f46', fontSize: 13, lineHeight: 1.5,
            marginBottom: 20,
          }}>
            🔒 <strong>Procesado 100% por Mercado Pago</strong><br/>
            Al confirmar te llevamos al checkout seguro de MP, donde elegís cómo pagar:
            tarjeta crédito/débito, Abitab, RedPagos, billetera MP o transferencia.
          </div>

          <Field label="Notas para el repartidor (opcional)">
            <textarea rows="2" value={form.notas}
              onChange={e => setForm({ ...form, notas: e.target.value })}
              placeholder="Llamar al portero, dejar en recepción, etc." />
          </Field>

          {err && <div className="cf-err">{err}</div>}

          <div className="cf-nav">
            <button className="cf-btn-back" onClick={() => setPaso(3)}>← Atrás</button>
            <button className="cf-btn-next" onClick={confirmar} disabled={creando}>
              {creando ? 'Creando pedido…' : `💳 Confirmar y pagar · $${totalFinal.toLocaleString('es-UY')}`}
            </button>
          </div>
        </div>
      )}

      {/* Aviso de privacidad — visible en los 4 pasos */}
      <div className="cf-privacy">
        🔒 Tus datos están protegidos. Solo los usamos para procesar tu pedido y nunca los compartimos con terceros.
      </div>

      <style>{styles}</style>
    </div>
  )
}

function Field({ label, required, children }) {
  return (
    <label className="cf-field">
      <b>{label} {required && <span className="cf-req">*</span>}</b>
      {children}
    </label>
  )
}

function Row({ label, children, big }) {
  return (
    <div className={`cf-row ${big ? 'is-big' : ''}`}>
      <span>{label}</span>
      <span className="cf-row-val">{children}</span>
    </div>
  )
}

function formatFechaLarga(f) {
  if (!f) return '—'
  return new Date(f + 'T12:00:00').toLocaleDateString('es-UY', { weekday: 'long', day: 'numeric', month: 'long' })
}

function formatFechaCorta(f) {
  if (!f) return '—'
  return new Date(f + 'T12:00:00').toLocaleDateString('es-UY', { weekday: 'short', day: 'numeric', month: 'short' })
}

/** Dropdown custom — sustituye al <select> nativo (que en Android es enorme).
 *  Trigger compacto + panel scrolleable abajo, con grupos En Ruta / Fuera de Ruta. */
function FechaDropdown({ opciones, valor, onSeleccionar, calcEnvio }) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef(null)

  // Cerrar al click fuera
  useEffect(() => {
    if (!open) return
    function onDoc(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('touchstart', onDoc)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('touchstart', onDoc)
    }
  }, [open])

  const enRuta = opciones.filter(o => o.enRuta)
  const fueraRuta = opciones.filter(o => !o.enRuta)
  const elegida = opciones.find(o => o.fechaEntrega === valor)

  function pick(o) {
    onSeleccionar(o)
    setOpen(false)
  }

  return (
    <div className="cf-dd" ref={wrapRef}>
      <button type="button" className={`cf-dd-trigger ${open ? 'is-open' : ''}`} onClick={() => setOpen(o => !o)}>
        <span className="cf-dd-trigger-text">
          {elegida
            ? <>{elegida.enRuta ? '🟢' : '🟡'} {formatFechaCorta(elegida.fechaEntrega)} · {(() => {
                const env = calcEnvio(elegida)
                return env > 0 ? `+$${env.toLocaleString('es-UY')}` : 'GRATIS'
              })()}</>
            : <span className="cf-dd-placeholder">— Elegí un día —</span>
          }
        </span>
        <span className="cf-dd-caret">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="cf-dd-panel">
          {enRuta.length > 0 && (
            <>
              <div className="cf-dd-grupo">🟢 Tu ruta normal · más barato</div>
              {enRuta.map(o => {
                const env = calcEnvio(o)
                const sel = o.fechaEntrega === valor
                return (
                  <button key={o.fechaEntrega} type="button" className={`cf-dd-opt ${sel ? 'is-sel' : ''}`} onClick={() => pick(o)}>
                    <span className="cf-dd-opt-dia">{formatFechaCorta(o.fechaEntrega)}</span>
                    <span className={`cf-dd-opt-precio ${env > 0 ? '' : 'is-free'}`}>
                      {env > 0 ? `+$${env.toLocaleString('es-UY')}` : 'GRATIS'}
                    </span>
                  </button>
                )
              })}
            </>
          )}
          {fueraRuta.length > 0 && (
            <>
              <div className="cf-dd-grupo">🟡 Otros días · viaje especial</div>
              {fueraRuta.map(o => {
                const env = calcEnvio(o)
                const sel = o.fechaEntrega === valor
                return (
                  <button key={o.fechaEntrega} type="button" className={`cf-dd-opt ${sel ? 'is-sel' : ''}`} onClick={() => pick(o)}>
                    <span className="cf-dd-opt-dia">⚡ {formatFechaCorta(o.fechaEntrega)}</span>
                    <span className="cf-dd-opt-precio">+${env.toLocaleString('es-UY')}</span>
                  </button>
                )
              })}
            </>
          )}
        </div>
      )}
    </div>
  )
}

const styles = `
:root {
  --cf-navy:  #1a3461;
  --cf-blue:  #1a5cb8;
  --cf-teal:  #00c4d4;
  --cf-cream: #F8FAFC;
  --cf-line:  #e2e8f0;
  --cf-muted: #64748b;
}
.cf-shell { max-width: 760px; margin: 0 auto; padding: 24px 16px 60px; font-family: 'Inter', system-ui, sans-serif; color: var(--cf-navy) }

/* Hero */
.cf-hero { text-align: center; margin-bottom: 22px; padding: 32px 16px; background: linear-gradient(135deg, #1a3461 0%, #1a5cb8 60%, #00c4d4 100%); color: #fff; border-radius: 18px }
.cf-hero-pill { display: inline-flex; align-items: center; gap: 8px; background: rgba(255,255,255,.12); border: 1px solid rgba(255,255,255,.18); padding: 6px 14px; border-radius: 999px; font-size: 11px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; margin-bottom: 12px }
.cf-hero-dot { width: 7px; height: 7px; background: var(--cf-teal); border-radius: 50%; animation: pulse 1.6s infinite }
@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.4} }
.cf-hero h1 { font-size: clamp(24px, 5vw, 36px); font-weight: 900; letter-spacing: -.02em; margin: 0 }

/* Stepper */
.cf-stepper { display: grid; grid-template-columns: repeat(4, 1fr); gap: 4px; margin-bottom: 18px }
.cf-step { text-align: center; opacity: .45 }
.cf-step.active, .cf-step.done { opacity: 1 }
.cf-step-num { display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px; border-radius: 50%; background: #e2e8f0; color: #64748b; font-weight: 800; font-size: 13px; margin-bottom: 4px }
.cf-step.active .cf-step-num { background: var(--cf-teal); color: var(--cf-navy) }
.cf-step.done .cf-step-num { background: #22c55e; color: #fff }
.cf-step-label { font-size: 10px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: .05em }
.cf-step.active .cf-step-label { color: var(--cf-navy) }

/* Card */
.cf-card { background: #fff; border-radius: 18px; padding: 24px; box-shadow: 0 8px 40px rgba(26,52,97,.08); border: 1px solid rgba(26,52,97,.05) }
.cf-h2 { font-size: 22px; font-weight: 900; color: var(--cf-navy); letter-spacing: -.01em; margin: 0 0 18px }
.cf-h3 { font-size: 14px; font-weight: 800; color: var(--cf-navy); letter-spacing: .02em; margin: 22px 0 10px; text-transform: uppercase; letter-spacing: .08em }
.cf-h3-row { display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap; margin: 22px 0 10px }
.cf-h3-row .cf-h3 { margin: 0 }
.cf-pill-cuenta { display: inline-flex; align-items: center; gap: 4px; padding: 4px 10px; border-radius: 999px; background: rgba(0,196,212,.12); color: #0e7490; font-size: 11px; font-weight: 800; letter-spacing: .04em }
.cf-visitor-aviso { background: linear-gradient(135deg, rgba(0,196,212,.10), rgba(245,158,11,.08)); border: 1px solid rgba(0,196,212,.30); border-radius: 10px; padding: 12px 14px; font-size: 12px; color: var(--cf-navy); margin-bottom: 12px; line-height: 1.5 }
.cf-hint { font-size: 12px; color: var(--cf-muted); margin: 6px 0 14px; line-height: 1.5; font-style: italic }
.cf-sub { color: #475569; font-size: 14px; margin: 0 0 18px }
.cf-loading { color: var(--cf-muted); font-size: 14px; padding: 16px 0 }

/* Items */
.cf-items { list-style: none; padding: 0; margin: 12px 0 16px; display: flex; flex-direction: column; gap: 10px }
.cf-items li { display: grid; grid-template-columns: 1fr auto auto; align-items: center; gap: 12px; padding: 12px; background: #f8fafc; border: 1px solid var(--cf-line); border-radius: 12px; font-size: 14px }
.cf-item-info strong { color: var(--cf-navy) }
.cf-item-info span { color: var(--cf-muted) }
.cf-item-marca { display: inline-block; font-size: 11px; padding: 2px 8px; background: #fff; border: 1px solid var(--cf-line); border-radius: 999px; margin-left: 6px }
.cf-item-cant { display: flex; align-items: center; gap: 8px; background: #fff; border: 1px solid var(--cf-line); border-radius: 999px; padding: 4px }
.cf-item-cant button { width: 26px; height: 26px; border-radius: 50%; border: 0; background: #f1f5f9; color: var(--cf-navy); font-weight: 800; cursor: pointer; font-family: inherit; line-height: 1 }
.cf-item-cant button:hover { background: var(--cf-teal); color: #fff }
.cf-item-cant span { font-weight: 800; min-width: 18px; text-align: center }
.cf-item-precio { font-weight: 900; color: var(--cf-navy); white-space: nowrap }
.cf-empty { color: var(--cf-muted); text-align: center; padding: 20px 0 }

/* Kit row (cuando hay un kit grande) */
.cf-kit-row { display: flex; justify-content: space-between; align-items: center; gap: 14px; padding: 18px; background: linear-gradient(135deg, rgba(0,196,212,.08), rgba(26,92,184,.08)); border: 1px solid rgba(0,196,212,.25); border-radius: 14px; margin-bottom: 10px }
.cf-kit-tag { font-size: 10px; font-weight: 800; color: var(--cf-teal); letter-spacing: .12em }
.cf-kit-nombre { font-size: 18px; font-weight: 900; color: var(--cf-navy); letter-spacing: -.01em; text-transform: capitalize; margin-top: 2px }
.cf-kit-sub { font-size: 12px; color: var(--cf-muted); margin-top: 4px }
.cf-kit-precio { font-size: 22px; font-weight: 900; color: var(--cf-navy); letter-spacing: -.02em }

/* Totales */
.cf-totales { margin-top: 18px; padding: 14px 16px; background: #f8fafc; border-radius: 12px }
.cf-totales > div:not(.cf-totales-info) { display: flex; justify-content: space-between; align-items: baseline; font-size: 15px }
.cf-totales > div strong { font-size: 22px; font-weight: 900; color: var(--cf-navy) }
.cf-totales-info { font-size: 12px; color: var(--cf-muted); margin-top: 8px }

/* Form */
.cf-field { display: block; margin-bottom: 14px }
.cf-field b { display: block; font-size: 11px; font-weight: 800; color: var(--cf-navy); letter-spacing: .08em; text-transform: uppercase; margin-bottom: 6px }
.cf-req { color: #dc2626 }
.cf-card input, .cf-card select, .cf-card textarea { width: 100%; box-sizing: border-box; padding: 11px 13px; font-size: 14px; border: 1.5px solid var(--cf-line); border-radius: 10px; background: #fff; color: var(--cf-navy); font-family: inherit; transition: border-color .15s, box-shadow .15s }
.cf-card input:focus, .cf-card select:focus, .cf-card textarea:focus { outline: none; border-color: var(--cf-teal); box-shadow: 0 0 0 3px rgba(0,196,212,.15) }
.cf-row-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px }
.cf-row-2-3 { display: grid; grid-template-columns: 2fr 1fr; gap: 12px }
@media (max-width: 520px) { .cf-row-2, .cf-row-2-3 { grid-template-columns: 1fr } }

/* Info */
.cf-info { background: #f0fcfd; border: 1px solid #b3eef3; border-radius: 10px; padding: 12px 14px; font-size: 13px; color: var(--cf-navy); margin-bottom: 12px }
.cf-err { background: #fef2f2; border: 1px solid #fca5a5; border-radius: 10px; padding: 12px 14px; font-size: 13px; color: #991b1b; margin-top: 12px }

/* Suscriptor: dirección "de siempre" (paso 2) */
.cf-direccion-siempre {
  display: flex; gap: 14px; align-items: flex-start;
  padding: 16px 18px; border-radius: 14px;
  background: linear-gradient(135deg, rgba(0,196,212,.10), rgba(26,92,184,.08));
  border: 1.5px solid rgba(0,196,212,.30);
  margin-bottom: 12px;
}
.cf-direccion-siempre-icon { font-size: 26px; line-height: 1; flex-shrink: 0 }
.cf-direccion-siempre-body { flex: 1 }
.cf-direccion-siempre-tag {
  font-size: 10px; font-weight: 800; color: var(--cf-blue);
  letter-spacing: .08em; text-transform: uppercase; margin-bottom: 4px;
}
.cf-direccion-siempre-linea {
  font-size: 16px; font-weight: 800; color: var(--cf-navy);
  letter-spacing: -.01em; line-height: 1.3;
}
.cf-direccion-siempre-barrio {
  font-size: 13px; color: var(--cf-muted); margin-top: 4px;
}
.cf-link-cambiar {
  display: inline-block; margin-bottom: 8px;
  background: transparent; border: 0;
  color: var(--cf-blue); font-weight: 700; font-size: 13px;
  text-decoration: underline; cursor: pointer;
  font-family: inherit; padding: 4px 0;
}
.cf-aviso-otra-direccion {
  display: flex; align-items: center; justify-content: space-between;
  gap: 10px; padding: 10px 14px; margin-bottom: 14px;
  background: #fefce8; border: 1px solid #fcd34d;
  border-radius: 10px; font-size: 12px; color: #78350f;
}
.cf-aviso-otra-direccion button {
  background: transparent; border: 0; color: var(--cf-navy);
  font-weight: 800; font-size: 12px; cursor: pointer;
  font-family: inherit; text-decoration: underline; white-space: nowrap;
}

/* Warning barrio detectado vs elegido (paso 2) */
.cf-warn-barrio {
  display: flex; gap: 12px; align-items: flex-start;
  margin-top: 14px; padding: 14px 16px;
  border-radius: 12px; font-size: 13px;
}
.cf-warn-barrio.is-warn {
  background: #fef3c7; border: 1.5px solid #fcd34d; color: #78350f;
}
.cf-warn-barrio.is-bloqueo {
  background: #fef2f2; border: 1.5px solid #fca5a5; color: #7f1d1d;
}
.cf-warn-barrio-icon { font-size: 24px; line-height: 1; flex-shrink: 0 }
.cf-warn-barrio-body { flex: 1 }
.cf-warn-barrio-body strong { display: block; font-size: 14px; margin-bottom: 6px }
.cf-warn-barrio-body p { margin: 0 0 10px; line-height: 1.45 }
.cf-warn-cta {
  display: inline-block;
  padding: 10px 18px; border-radius: 999px;
  background: var(--cf-navy); color: #fff !important; text-decoration: none;
  border: 0; font-weight: 800; font-size: 13px; cursor: pointer;
  font-family: inherit; margin-right: 8px;
}
.cf-warn-cta:hover { background: var(--cf-blue) }
.cf-warn-link {
  display: block; margin-top: 10px;
  background: transparent; border: 0;
  color: inherit; opacity: .65;
  font-size: 11px; font-weight: 600;
  text-decoration: underline; cursor: pointer;
  font-family: inherit; padding: 4px 0;
}

.cf-confirm-pin { display: flex; align-items: center; gap: 10px; margin-top: 12px; padding: 10px 14px; border-radius: 10px; font-size: 13px; font-weight: 600; cursor: pointer }
.cf-confirm-pin.ok   { border: 1.5px solid #22c55e; background: #f0fdf4; color: #166534 }
.cf-confirm-pin.pend { border: 1.5px solid #f59e0b; background: #fefce8; color: #854d0e }
.cf-confirm-pin input { width: 16px !important; height: 16px; margin: 0 }

.cf-btn-secondary { padding: 10px 18px; font-size: 13px; font-weight: 700; background: #fff; color: var(--cf-navy); border: 1.5px solid var(--cf-navy); border-radius: 999px; cursor: pointer; font-family: inherit; transition: background .15s, color .15s }
.cf-btn-secondary:hover:not(:disabled) { background: var(--cf-navy); color: #fff }
.cf-btn-secondary:disabled { opacity: .5; cursor: not-allowed }

/* Selector único de día (paso 3) — dropdown custom */
.cf-dd { position: relative; width: 100% }
.cf-dd-trigger {
  width: 100%; box-sizing: border-box;
  display: flex; align-items: center; justify-content: space-between;
  gap: 8px; padding: 12px 14px; font-size: 14px; font-family: inherit;
  border: 1.5px solid var(--cf-line); border-radius: 10px;
  background: #fff; color: var(--cf-navy); cursor: pointer;
  text-align: left; transition: border-color .15s, box-shadow .15s;
}
.cf-dd-trigger:hover { border-color: var(--cf-teal) }
.cf-dd-trigger.is-open { border-color: var(--cf-teal); box-shadow: 0 0 0 3px rgba(0,196,212,.15) }
.cf-dd-trigger-text { flex: 1; font-weight: 700; text-transform: capitalize; overflow: hidden; text-overflow: ellipsis; white-space: nowrap }
.cf-dd-placeholder { color: var(--cf-muted); font-weight: 500; text-transform: none }
.cf-dd-caret { font-size: 10px; color: var(--cf-muted); flex-shrink: 0 }

.cf-dd-panel {
  position: absolute; top: calc(100% + 4px); left: 0; right: 0; z-index: 50;
  background: #fff; border: 1.5px solid var(--cf-line); border-radius: 10px;
  box-shadow: 0 12px 32px rgba(15,31,61,.12);
  max-height: 280px; overflow-y: auto; padding: 4px;
}
.cf-dd-grupo {
  font-size: 10px; font-weight: 800; color: var(--cf-muted);
  letter-spacing: .06em; text-transform: uppercase;
  padding: 8px 10px 4px;
}
.cf-dd-opt {
  width: 100%; box-sizing: border-box;
  display: flex; justify-content: space-between; align-items: center;
  gap: 10px; padding: 9px 10px; margin: 1px 0;
  background: transparent; border: 0; border-radius: 8px;
  font-family: inherit; font-size: 13px; color: var(--cf-navy);
  cursor: pointer; text-align: left;
}
.cf-dd-opt:hover { background: #f0fcfd }
.cf-dd-opt.is-sel { background: rgba(0,196,212,.12); font-weight: 800 }
.cf-dd-opt-dia { text-transform: capitalize; flex: 1 }
.cf-dd-opt-precio { font-weight: 800; font-size: 12px; color: var(--cf-navy); white-space: nowrap }
.cf-dd-opt-precio.is-free { color: #15803d }

/* Resumen del día elegido (paso 3) */
.cf-fecha-resumen {
  display: flex; justify-content: space-between; align-items: center;
  gap: 12px; padding: 14px 16px; border-radius: 12px;
  margin-top: -4px; margin-bottom: 14px;
  border: 1.5px solid var(--cf-line);
}
.cf-fecha-resumen.is-en-ruta { background: #f0fcfd; border-color: rgba(0,196,212,.4) }
.cf-fecha-resumen.is-fuera   { background: #fefce8; border-color: rgba(245,158,11,.4) }
.cf-fecha-resumen-tag { font-size: 11px; font-weight: 800; letter-spacing: .04em; color: var(--cf-muted); text-transform: uppercase }
.cf-fecha-resumen-dia { font-size: 16px; font-weight: 800; color: var(--cf-navy); text-transform: capitalize; margin-top: 2px }
.cf-fecha-resumen-precio { white-space: nowrap; font-size: 14px; font-weight: 800; color: var(--cf-navy) }
.cf-fecha-resumen-precio .cf-monto-ok { color: #15803d }

/* Banner suscriptor / no-suscriptor (paso 3) */
.cf-banner-susc, .cf-banner-info {
  display: flex; align-items: flex-start; gap: 12px;
  padding: 14px 16px; border-radius: 12px;
  margin-bottom: 18px; font-size: 13px; line-height: 1.5;
}
.cf-banner-susc { background: linear-gradient(135deg, rgba(245,158,11,.12), rgba(0,196,212,.10)); border: 1px solid rgba(245,158,11,.3); color: var(--cf-navy) }
.cf-banner-info { background: #f0fcfd; border: 1px solid rgba(0,196,212,.25); color: var(--cf-navy) }
.cf-banner-icon { font-size: 22px; line-height: 1; flex-shrink: 0 }
.cf-banner-susc strong, .cf-banner-info strong { display: block; margin-bottom: 4px; font-size: 14px }

/* Fechas (paso 3) */
.cf-fecha-grupo { font-size: 13px; font-weight: 800; color: var(--cf-navy); margin: 14px 0 4px; letter-spacing: .02em }
.cf-fecha-grupo-sub { font-size: 12px; color: var(--cf-muted); margin: 0 0 10px; line-height: 1.45 }
.cf-fechas { display: flex; flex-direction: column; gap: 10px; margin-bottom: 14px }
.cf-fecha { display: flex; justify-content: space-between; align-items: center; gap: 12px; padding: 14px 16px; background: #fff; border: 1.5px solid var(--cf-line); border-radius: 12px; cursor: pointer; font-family: inherit; text-align: left; transition: border-color .15s, background .15s }
.cf-fecha:hover { border-color: var(--cf-teal); background: #f8fafc }
.cf-fecha.is-elegida { border-color: var(--cf-teal); box-shadow: 0 0 0 3px rgba(0,196,212,.15); background: #f0fcfd }
.cf-fecha.is-fuera { border-style: dashed; background: #fefdf8 }
.cf-fecha.is-fuera:hover { background: #fef3c7 }
.cf-fecha.is-fuera.is-elegida { border-color: #f59e0b; background: #fef3c7; box-shadow: 0 0 0 3px rgba(245,158,11,.18) }
.cf-fecha-dia { font-size: 15px; font-weight: 800; color: var(--cf-navy); text-transform: capitalize }
.cf-fecha-sub { font-size: 12px; color: var(--cf-muted); margin-top: 2px }
.cf-fecha-precio { white-space: nowrap; font-size: 13px; font-weight: 800 }
.cf-monto-ok   { color: #15803d }
.cf-monto-mid  { color: var(--cf-blue) }
.cf-monto-warn { color: #b45309 }

/* Resumen paso 4 */
.cf-resumen { background: #f8fafc; border-radius: 12px; padding: 14px 18px; margin-bottom: 4px }
.cf-row { display: flex; justify-content: space-between; align-items: baseline; padding: 8px 0; font-size: 14px }
.cf-row + .cf-row { border-top: 1px dashed var(--cf-line) }
.cf-row span:first-child { color: var(--cf-muted); font-weight: 600 }
.cf-row-val { color: var(--cf-navy); text-align: right }
.cf-row.is-big { padding-top: 14px; margin-top: 8px; border-top: 2px solid var(--cf-navy) }
.cf-row.is-big span:first-child { font-size: 13px; color: var(--cf-navy); font-weight: 800; text-transform: uppercase; letter-spacing: .06em }
.cf-row.is-big .cf-row-val { font-size: 22px; font-weight: 900; color: var(--cf-navy) }

/* Pagos */
.cf-pagos { display: flex; flex-direction: column; gap: 8px; margin-bottom: 14px }
.cf-pago { display: flex; align-items: center; gap: 12px; padding: 12px 14px; border: 1.5px solid var(--cf-line); border-radius: 12px; cursor: pointer; transition: border-color .15s, background .15s; background: #fff }
.cf-pago:hover { border-color: var(--cf-teal) }
.cf-pago.is-elegido { border-color: var(--cf-teal); background: #f0fcfd; box-shadow: 0 0 0 3px rgba(0,196,212,.15) }
.cf-pago.is-soon { opacity: .55; cursor: not-allowed }
.cf-pago.is-soon:hover { border-color: var(--cf-line) }
.cf-pago input { width: 18px !important; height: 18px; margin: 0; flex-shrink: 0 }
.cf-pago > div { display: flex; flex-direction: column; gap: 2px; flex: 1 }
.cf-pago strong { font-size: 14px; color: var(--cf-navy); font-weight: 800 }
.cf-pago span { font-size: 12px; color: var(--cf-muted) }
.cf-soon { display: inline-block; font-style: normal; font-size: 10px; font-weight: 800; color: var(--cf-blue); text-transform: uppercase; letter-spacing: .08em; margin-top: 4px }

/* Nav */
.cf-nav { display: flex; justify-content: space-between; gap: 10px; margin-top: 22px; padding-top: 18px; border-top: 1px solid var(--cf-line) }
.cf-btn-back { padding: 12px 20px; background: transparent; border: 0; color: var(--cf-muted); font-weight: 700; font-size: 14px; cursor: pointer; border-radius: 999px; font-family: inherit }
.cf-btn-back:hover { background: #f1f5f9; color: var(--cf-navy) }
.cf-btn-next { padding: 14px 22px; font-size: 14px; font-weight: 800; background: linear-gradient(135deg, var(--cf-blue), var(--cf-teal)); color: #fff; border: 0; border-radius: 999px; cursor: pointer; font-family: inherit; box-shadow: 0 6px 18px rgba(0,196,212,.25); transition: transform .12s, box-shadow .12s }
.cf-btn-next:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 8px 22px rgba(0,196,212,.35) }
.cf-btn-next:disabled { opacity: .45; cursor: not-allowed; box-shadow: none }

.cf-link { background: none; border: 0; color: var(--cf-blue); cursor: pointer; font-weight: 700; padding: 0; font-family: inherit }

/* Aviso de privacidad (footer del checkout, los 4 pasos) */
.cf-privacy {
  margin-top: 18px; padding: 12px 16px;
  font-size: 11px; line-height: 1.5;
  color: var(--cf-muted); text-align: center;
  background: rgba(248,250,252,.6);
  border-radius: 10px;
  max-width: 540px; margin-left: auto; margin-right: auto;
}
`
